import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import { Icon } from '@ur-solutions/react-components'
import styled from 'styled-components'

import { DetectBlur } from '@/containers/util-containers'
import { type VehicleGroupForm } from '../../types'
import { Input, RegularModal } from '@/components_deprecated'
import { Form } from '@/components_deprecated/Form'
import { Table } from '@/components_deprecated/Table'
import { SelectMultipleUsers } from '@/components_deprecated/Select/SelectMultipleUsers'
import VehicleSelect from '@/components/vehicle-select/vehicle-select'
import { type VehicleSelectNode } from '@/modules/vehicles'

interface FieldProps {
  alignLeft?: boolean
  alignRight?: boolean
  area?: string
}
const Field = styled.div<FieldProps>`
  grid-area: ${props => props.area};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${props => props.alignLeft && 'align-items: flex-start;'}
  ${props => props.alignRight && 'align-items: flex-end;'}

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

const StyledForm = styled(Form)`
  gap: 0.5rem;
`

const DoubleField = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
`

const StyledTable = styled(Table)`
  max-height: 300px;
  overflow-y: auto;
`

interface CreateOrEditGroupProps {
  initialValues?: VehicleGroupForm
  onSubmit: (data: VehicleGroupForm | null) => void
}

const CreateOrEditVehicleGroupModal = (props: CreateOrEditGroupProps) => {
  const { initialValues, onSubmit } = props

  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('')

  const translations = useTranslate({
    label: {
      name: 'common.name',
      users: 'common.users',
      vehicles: 'common.vehicles',
      search: 'common.search',
    },

    create: 'common.create-alt',
    edit: 'common.edit',
    update: 'common.update',

    error: {
      required: 'common.required',
    },
  })

  const {
    formValues: values,
    formChangeHandler: setField,
    formValid: isFormValid,
  } = useForm<VehicleGroupForm>({
    values: initialValues ?? {
      name: '',
      users: [],
      vehicles: [],
    },
    config: {
      initAsInvalid: true,
    },
    validators: {
      name: () => {
        if (!values.name.trim()) return translations.error.required
        return null
      },
    },
  })

  const normalizedVehicleSearchQuery = vehicleSearchQuery.trim().toLowerCase()
  const setVehicles = setField('vehicles')
  const setUsers = setField('users')
  const setName = setField('name')
  const selectedVehicleIds = useMemo(
    () => values.vehicles.map(vehicle => vehicle.id),
    [values.vehicles]
  )

  // Prevent duplicates and normalize displayed name fallback for selected rows.
  const handleVehicleSelect = (vehicle: VehicleSelectNode | null) => {
    if (!vehicle) return

    const alreadySelected = values.vehicles.some(
      selectedVehicle => selectedVehicle.id === vehicle.id
    )
    if (alreadySelected) return

    setVehicles([
      {
        id: vehicle.id,
        name: vehicle.registrationNumber || vehicle.vin || vehicle.id,
      },
      ...values.vehicles,
    ])
  }

  const handleVehicleSearchInput = (searchValue: string) => {
    setVehicleSearchQuery(searchValue)
  }

  const handleRemoveVehicle = (vehicleId: string) => {
    setVehicles(values.vehicles.filter(vehicle => vehicle.id !== vehicleId))
  }

  const visibleVehicles = useMemo(
    () =>
      values.vehicles
        .filter(vehicle => {
          if (!normalizedVehicleSearchQuery) return true
          return vehicle.name
            .toLowerCase()
            .includes(normalizedVehicleSearchQuery)
        })
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [values.vehicles, normalizedVehicleSearchQuery]
  )

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <RegularModal
        title={initialValues ? translations.edit : translations.create}
        submitText={translations.update}
        submitDisabled={!isFormValid}
        onCancel={() => onSubmit(null)}
        onSubmit={() => onSubmit(values)}
        width="600px"
      >
        <StyledForm onSubmit={() => onSubmit(values)}>
          <Field>
            <label>{translations.label.name}</label>
            <Input value={values.name} onChange={setName} grayed />
          </Field>

          <Field>
            <label>{translations.label.users}</label>
            <SelectMultipleUsers
              selected={values.users}
              onChange={setUsers}
              grayed
            />
          </Field>

          <DoubleField>
            <Field>
              <label>{translations.label.vehicles}</label>
              <VehicleSelect
                mode="single"
                value={''}
                excludeVehicleIds={selectedVehicleIds}
                onChange={handleVehicleSelect}
                PopoverContentProps={{ onMouseUp: e => e.stopPropagation() }}
              />
            </Field>

            <Field>
              <label>{translations.label.search}</label>
              <Input
                value={vehicleSearchQuery}
                onChange={handleVehicleSearchInput}
                grayed
                placeholder={translations.label.search}
              />
            </Field>
          </DoubleField>

          <StyledTable headerMargin noData={visibleVehicles.length === 0}>
            <thead>
              <tr>
                <th>{translations.label.name}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleVehicles.map(vehicle => (
                <tr key={vehicle.id}>
                  <td>{vehicle.name}</td>
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleRemoveVehicle(vehicle.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </StyledForm>
      </RegularModal>
    </DetectBlur>
  )
}

export default CreateOrEditVehicleGroupModal
